import React, { useEffect, useState } from 'react';
import { Info, Edit, Plus, RefreshCw, Trash2, Bell } from 'lucide-react';
import http from '../api/http';
import { useAuth } from '../context/AuthContext';
import BlueHeader from '../components/BlueHeader';

const MisesAJour = () => {
  const { user } = useAuth();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await http.get('/api/mises-a-jour');
      setData(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erreur:', error);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({ actif: 1 });
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette mise à jour ?')) return;

    try {
      await http.delete(`/api/mises-a-jour/${id}`);
      fetchData();
    } catch (error) {
      alert('Erreur lors de la suppression: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await http.put(`/api/mises-a-jour/${editingItem.id}`, formData);
      } else {
        await http.post('/api/mises-a-jour', formData);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Erreur: ' + (error.response?.data?.error || 'Erreur inconnue'));
    }
  };

  return (
    <div>
      <BlueHeader
        title="Mises à jour"
        subtitle="Gestion des mises à jour"
        icon={Info}
        showBack={false}
      />
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-[#0F1E3D]">Mises à jour</h2>
          <div className="flex gap-2">
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-[#E3A81C] text-[#0F1E3D] rounded-lg hover:bg-[#EDB52E] transition-colors font-medium"
            >
              <Plus className="w-4 h-4" />
              Créer
            </button>
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>
        </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            Chargement…
          </div>
        ) : data.length === 0 ? (
          <div className="px-4 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
            Aucune mise à jour.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Version</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Titre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Date publication</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{item.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{item.version || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white font-medium">{item.titre}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">{item.type || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {item.date_publication ? new Date(item.date_publication).toLocaleDateString('fr-FR') : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {item.actif ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-900 px-2 py-0.5 text-xs font-medium text-green-600 dark:text-green-300">
                          <Bell className="h-3 w-3 mr-1" />
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex gap-2">
                        <button type="button" onClick={() => handleEdit(item)} className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button type="button" onClick={() => handleDelete(item.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-blue-100">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4">
              <h3 className="text-lg font-bold text-blue-900">{editingItem ? 'Modifier' : 'Créer'} Mise à jour</h3>
              <button type="button" onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Version</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.version || ''}
                    onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                    required
                    placeholder="Ex: 1.2.3"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Titre</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.titre || ''}
                    onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                    required
                    placeholder="Ex: Correction bug paie"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Description</label>
                  <textarea
                    className="input-field"
                    value={formData.description || ''}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows="4"
                    required
                    placeholder="Détail de la mise à jour ou correction..."
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Type</label>
                  <select
                    className="input-field"
                    value={formData.type || 'feature'}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  >
                    <option value="feature">Nouvelle fonctionnalité</option>
                    <option value="bugfix">Correction de bug</option>
                    <option value="improvement">Amélioration</option>
                    <option value="security">Sécurité</option>
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Filiale cible</label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.filiale_cible || 'all'}
                    onChange={(e) => setFormData({ ...formData, filiale_cible: e.target.value })}
                    placeholder="all ou code filiale (ex: BJ, CI)"
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Date publication</label>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={formData.date_publication || ''}
                    onChange={(e) => setFormData({ ...formData, date_publication: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Date expiration</label>
                  <input
                    type="datetime-local"
                    className="input-field"
                    value={formData.date_expiration || ''}
                    onChange={(e) => setFormData({ ...formData, date_expiration: e.target.value })}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-night-blue">Priorité</label>
                  <select
                    className="input-field"
                    value={formData.priorite || 'medium'}
                    onChange={(e) => setFormData({ ...formData, priorite: e.target.value })}
                  >
                    <option value="low">Basse</option>
                    <option value="medium">Moyenne</option>
                    <option value="high">Haute</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.actif}
                    onChange={(e) => setFormData({ ...formData, actif: e.target.checked ? 1 : 0 })}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label className="text-sm text-gray-700 dark:text-gray-300">Afficher aux utilisateurs à la connexion</label>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-blue-100 pt-4">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300">
                  Annuler
                </button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                  {editingItem ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default MisesAJour;
